/**
 * Canonical entity identity for DrSullySpeaks structured data.
 *
 * Every Book / CreativeWork / Course / Service node across the site points its
 * `author` and `publisher` at these two @ids, so search engines merge all of the
 * information products into ONE author entity and ONE brand entity instead of
 * treating each page as a separate, unconnected publisher.
 *
 * The same @ids are declared on the homepage (index.html) where the full Person
 * and Organization descriptions live.
 */

export const SITE_URL = "https://drsullyspeaks.com";

/** Sitewide Person @id — Dr. RD "Sully" Sullivan. */
export const PERSON_ID = `${SITE_URL}/#person`;
/** Sitewide Organization @id — DrSullySpeaks. */
export const ORG_ID = `${SITE_URL}/#organization`;
/** MakeChange.ai brand/organization @id — the product & framework arm. */
export const MAKECHANGE_ORG_ID = `${SITE_URL}/#makechange-ai`;
/** Sitewide WebSite @id. */
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Verified profile set — keep in sync with index.html sameAs blocks. */
export const SAME_AS: readonly string[] = [
  "https://instagram.com/DrSullySpeaks",
  "https://www.linkedin.com/in/drsullyspeaks/",
  "https://x.com/DrSullySpeaks",
  "https://youtube.com/@DrSullySpeaks",
  "https://tiktok.com/@DrSullySpeaks",
  "https://www.facebook.com/DrSullySpeak",
  "https://www.facebook.com/DrSullySpeaksTruth",
  "https://substack.com/@drsullyspeaks",
  "https://stan.store/DrSullySpeaks",
  "https://books.by/drsullyspeaks",
  "https://www.lulu.com/spotlight/drsullyspeaks",
];

/**
 * MakeChange.ai public properties. The brand publishes through the SAME social
 * handles as DrSullySpeaks, so those profiles are included here too — that is
 * what lets search engines reconcile the brand with the verified accounts
 * instead of treating MakeChange.ai as an unlinked, unverified name.
 */
export const MAKECHANGE_SAME_AS: readonly string[] = [
  "https://makechangenow.pro",
  "https://makechangenow.lovable.app",
  ...SAME_AS,
];


/**
 * Canonical public contact channels. These are the REAL published details
 * already used in the LocalBusiness / ProfessionalService markup and on the
 * contact page — structured data must never carry invented placeholders like
 * "+1-000-000-0000" or "email@example.com", because Google treats fabricated
 * contact info as a spam signal and it can surface verbatim in rich results.
 *
 * To change a channel, edit it HERE and mirror the same value into the static
 * blocks in index.html (they cannot import from TypeScript).
 */
export const CONTACT = {
  /** E.164 format — required by schema.org for international resolution. */
  telephone: "+1-855-762-6224",
  email: "DrSully@DrSullySpeaks.com",
} as const;

/**
 * ContactPoint set for the brand entities. Each entry is a distinct channel so
 * search engines (and AI assistants answering "how do I contact MakeChange.ai")
 * can route the intent correctly instead of guessing from one generic number.
 */
export const contactPoints = () => [
  {
    "@type": "ContactPoint",
    contactType: "sales",
    name: "Speaking, coaching & consulting inquiries",
    telephone: CONTACT.telephone,
    email: CONTACT.email,
    url: `${SITE_URL}/inquire`,
    areaServed: "Worldwide",
    availableLanguage: ["English"],
  },
  {
    "@type": "ContactPoint",
    contactType: "customer support",
    name: "Product, masterclass & book support",
    email: CONTACT.email,
    // No dedicated /contact route exists — /inquire is the live intake form.
    url: `${SITE_URL}/inquire`,
    areaServed: "Worldwide",
    availableLanguage: ["English"],
  },
  {
    "@type": "ContactPoint",
    contactType: "media",
    name: "Press & media requests",
    email: CONTACT.email,
    // /pr-template is the published press/media resource page.
    url: `${SITE_URL}/pr-template`,
    areaServed: "Worldwide",
    availableLanguage: ["English"],
  },
];

/** Reference stubs — use these inside product nodes. */
export const authorRef = { "@id": PERSON_ID } as const;
export const publisherRef = { "@id": ORG_ID } as const;
/** Reference stub for MakeChange.ai. */
export const makeChangeRef = { "@id": MAKECHANGE_ORG_ID } as const;

/** Full Person node. Emit once per page graph. */
export const personNode = () => ({
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Dr. RD 'Sully' Sullivan",
  alternateName: ["Dr. Sully", "DrSullySpeaks", "Dr. Sully Speaks"],
  jobTitle: "Executive Coach, Speaker & Author",
  url: `${SITE_URL}/about`,
  image: `${SITE_URL}/favicon.png`,
  honorificSuffix: "PsyD · ThD · DMin · CERC",
  knowsAbout: [
    "Industrial-Organizational Psychology",
    "Trauma-Informed Leadership",
    "Executive Coaching",
    "AI Strategy",
    "MAKECHANGE Framework",
  ],
  worksFor: publisherRef,
  // Ties the person to BOTH brand entities so search engines merge them.
  affiliation: [publisherRef, makeChangeRef],
  founder: [publisherRef, makeChangeRef],
  sameAs: [...SAME_AS],
});

/** Full Organization node. Emit once per page graph. */
export const organizationNode = () => ({
  "@type": "Organization",
  "@id": ORG_ID,
  name: "DrSullySpeaks",
  alternateName: "He Speaks Organic Coaching & Consulting LLC",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  founder: authorRef,
  subOrganization: makeChangeRef,
  email: CONTACT.email,
  telephone: CONTACT.telephone,
  contactPoint: contactPoints(),
  sameAs: [...SAME_AS],
});

/**
 * MakeChange.ai — the framework/product arm behind the MAKECHANGE Operating
 * System. Declared as its own Organization + Brand so the name resolves to a
 * real entity connected to Dr. Sully rather than a floating product string.
 */
export const makeChangeOrganizationNode = () => ({
  "@type": ["Organization", "Brand"],
  "@id": MAKECHANGE_ORG_ID,
  name: "MakeChange.ai",
  alternateName: ["MakeChange", "Make Change Now", "MAKECHANGE Operating System"],
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  description:
    "MakeChange.ai is the AI-enabled change framework arm of DrSullySpeaks — home of the 10-pillar MAKECHANGE Operating System (Momentum, Authority, Kingdom-Focus, Equity, Clarity, Healing, Action, Navigation, Growth, Empowerment) used in executive coaching, consulting, and leadership masterclasses.",
  founder: authorRef,
  parentOrganization: publisherRef,
  knowsAbout: [
    "MAKECHANGE Operating System",
    "AI-enabled organizational change",
    "Leadership development",
    "Trauma-informed coaching",
  ],
  areaServed: "Worldwide",
  email: CONTACT.email,
  telephone: CONTACT.telephone,
  contactPoint: contactPoints(),
  sameAs: [...MAKECHANGE_SAME_AS],
});

/**
 * Route that actually serves site search. Google only shows a sitelinks
 * searchbox when the SearchAction target is a REAL working URL — keep this in
 * sync with the /search route in App.tsx and the block in index.html.
 */
export const SEARCH_PATH = "/search";

/**
 * Sitewide WebSite node with a SearchAction (sitelinks searchbox eligibility).
 * Emit once per page graph; the homepage and /search both carry it.
 */
export const websiteNode = () => ({
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "DrSullySpeaks",
  alternateName: ["Dr. Sully Speaks", "MakeChange.ai"],
  url: `${SITE_URL}/`,
  inLanguage: "en-US",
  publisher: publisherRef,
  copyrightHolder: publisherRef,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}${SEARCH_PATH}?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});




/**
 * Node for the /search route itself. The sitewide WebSite + SearchAction is
 * already emitted from index.html on every route, so this page references that
 * node by @id instead of restating it — one WebSite entity, not two.
 */
export const searchResultsPageNode = () => ({
  "@type": "SearchResultsPage",
  "@id": `${SITE_URL}${SEARCH_PATH}#webpage`,
  url: `${SITE_URL}${SEARCH_PATH}`,
  name: "Search DrSullySpeaks.com",
  isPartOf: { "@id": WEBSITE_ID },
  inLanguage: "en-US",
  publisher: publisherRef,
});

/**
 * Social identity used by BOTH the static index.html head and the per-route
 * <PageMeta> head. Single source of truth so every platform (Google, X,
 * Facebook/LinkedIn OG crawlers) resolves the same author + profile.
 * Validated by scripts/validate-social-preview.ts and
 * src/test/social-preview-identity.test.tsx.
 */
export const SOCIAL_IDENTITY = {
  authorName: "Dr. RD 'Sully' Sullivan",
  siteName: "Dr. Sully Speaks",
  twitterHandle: "@DrSullySpeaks",
  /** Facebook Page used for og article:publisher. */
  facebookPage: "https://www.facebook.com/DrSullySpeak",
  /** Facebook personal profile used for og article:author. */
  facebookProfile: "https://www.facebook.com/DrSullySpeaksTruth",
  profileUrl: `${SITE_URL}/about`,
} as const;
